import type { ReactNode } from "react";

/**
 * Schlichte Markdown-Anzeige für Dateien aus den Repos (Bestandsaufnahme): Überschriften, Absätze, Listen,
 * Tabellen, **fett** und `Code`. Ohne HTML aus der Datei: alles wird als Text gesetzt, nichts wird ausgeführt.
 */
function inline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|`([^`]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) parts.push(<strong key={i++}>{m[1]}</strong>);
    else parts.push(<code key={i++}>{m[2]}</code>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

const cells = (line: string) =>
  line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());

export function Markdown({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (!line.trim()) {
      i += 1;
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = Math.min((h[1]?.length ?? 1) + 2, 6);
      const Tag = `h${level}` as "h3" | "h4" | "h5" | "h6";
      blocks.push(<Tag key={k++}>{inline(h[2] ?? "")}</Tag>);
      i += 1;
      continue;
    }
    if (line.trim().startsWith("|")) {
      const rows: string[][] = [];
      while (i < lines.length && (lines[i] ?? "").trim().startsWith("|")) {
        const r = cells(lines[i] ?? "");
        if (!r.every((c) => /^:?-{2,}:?$/.test(c))) rows.push(r);
        i += 1;
      }
      const [head, ...body] = rows;
      blocks.push(
        <div className="md-table" key={k++}>
          <table>
            <thead>
              <tr>
                {(head ?? []).map((c, j) => (
                  <th key={j}>{inline(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, j) => (
                    <td key={j}>{inline(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    const isItem = (l: string) => /^\s*(?:[-*]|\d+[.)])\s+/.test(l);
    if (isItem(line)) {
      const ordered = /^\s*\d/.test(line);
      const items: string[] = [];
      while (i < lines.length && (isItem(lines[i] ?? "") || /^\s{2,}\S/.test(lines[i] ?? ""))) {
        const l = lines[i] ?? "";
        if (isItem(l)) items.push(l.replace(/^\s*(?:[-*]|\d+[.)])\s+/, ""));
        else items[items.length - 1] += ` ${l.trim()}`;
        i += 1;
      }
      const List = ordered ? "ol" : "ul";
      blocks.push(
        <List key={k++}>
          {items.map((it, j) => (
            <li key={j}>{inline(it)}</li>
          ))}
        </List>,
      );
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? "").trim() &&
      !/^(#{1,4}\s|\s*\||\s*(?:[-*]|\d+[.)])\s)/.test(lines[i] ?? "")
    ) {
      para.push((lines[i] ?? "").trim());
      i += 1;
    }
    blocks.push(<p key={k++}>{inline(para.join(" "))}</p>);
  }
  return <div className="md">{blocks}</div>;
}
